import { ReactElement, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Checkbox,
  Field,
  Heading,
  Input,
  Label,
  Link,
  Paragraph,
  Select,
  Skeleton,
  Tag,
  Textarea,
  ValidationMessage,
} from '@digdir/designsystemet-react';
import { PadlockLockedIcon, XMarkIcon } from '@navikt/aksel-icons';
import type { PlanDetail } from '../../../api/safetyRounds';
import {
  usePlanQuery,
  useSaveCustomizationMutation,
  useSuggestCheckpointMutation,
} from '../../../hooks/queries/safetyRounds/usePlanQueries';
import {
  addExtra,
  customizationInput,
  includedCount,
  planPath,
  removeExtra,
  toCustomizationForm,
  toggleHidden,
} from './plansView';
import { ApiErrorUtils } from '../../../utils/api-error.utils';
import { LoadProblem } from '../LoadProblem';
import layout from '../../HRDashboard/hmsPage.module.css';
import classes from './Plans.module.css';

/** «Tilpass sjekklisten»: the checklist of one plan, fitted to its area */
export const PlanChecklistPage = (): ReactElement => {
  const { t } = useTranslation();
  const id = Number(useParams().planId);
  const { data, isLoading, error } = usePlanQuery(id);

  // Without access, or for a plan that does not exist, the page is not shown as an empty form
  if (ApiErrorUtils.pageProblemOf(error)) return <LoadProblem error={error} subject="plan" />;

  return (
    <div className={layout.page} data-size="md">
      <section className={layout.hero}>
        <div aria-hidden="true" className={layout.backdrop} />
        <div className={layout.heroContent}>
          <Link asChild data-size="sm" className={layout.startAligned}>
            <RouterLink to={planPath(id)}>{t('safetyRounds.customize.back')}</RouterLink>
          </Link>
          <span className={layout.eyebrow}>{data?.name ?? t('safetyRounds.plans.title')}</span>
          <Heading level={1} data-size="xl">
            {t('safetyRounds.customize.title')}
          </Heading>
          {data && (
            <Paragraph className={layout.intro}>
              {t('safetyRounds.customize.intro', { name: data.template.name, version: data.template.version })}
            </Paragraph>
          )}
        </div>
      </section>
      {isLoading ? (
        <div aria-busy="true" className={layout.loading}>
          <span className="ds-sr-only">{t('safetyRounds.plan.loading')}</span>
          <Skeleton variant="rectangle" width="100%" height={420} />
        </div>
      ) : error || !data ? (
        <LoadProblem error={error} subject="plan" />
      ) : !data.mayCustomize ? (
        <div className={layout.loading}>
          <Alert data-color="info">
            <Paragraph>{t('safetyRounds.customize.onlySafetyRep')}</Paragraph>
          </Alert>
        </div>
      ) : (
        <Customize plan={data} />
      )}
    </div>
  );
};

const Customize = ({ plan }: { plan: PlanDetail }): ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form, setForm] = useState(toCustomizationForm(plan.checklist));
  const [adding, setAdding] = useState<Record<string, string>>({});
  const save = useSaveCustomizationMutation(plan.id);
  const K = 'safetyRounds.customize';

  return (
    <div className={layout.layout}>
      <div className={layout.main}>
        <Paragraph data-size="sm" role="status">
          {t(`${K}.count`, { count: includedCount(plan.checklist, form) })}
        </Paragraph>
        {plan.checklist.map((category) => {
          const extras = form.extra.filter((point) => point.categoryKey === category.key);
          const inputId = `customize-add-${category.key}`;
          return (
            <section
              key={category.key}
              aria-labelledby={`customize-${category.key}`}
              className={layout.card}
              data-gap="md"
            >
              <Heading level={2} data-size="xs" id={`customize-${category.key}`}>
                {category.number}. {category.name}
              </Heading>
              <ul className={classes.points}>
                {category.checkpoints
                  .filter((point) => point.source === 'TEMPLATE' && point.key)
                  .map((point) => {
                    const key = point.key as string;
                    return (
                      <li key={key} className={classes.point}>
                        {point.required ? (
                          <div className={classes.locked}>
                            <PadlockLockedIcon aria-hidden="true" />
                            <div className={classes.lockedText}>
                              <Paragraph data-size="sm">{point.text}</Paragraph>
                              <Tag data-color="neutral" data-size="sm">
                                {t(`${K}.requiredByHr`)}
                              </Tag>
                            </div>
                          </div>
                        ) : (
                          <Checkbox
                            data-size="sm"
                            label={point.text}
                            checked={!form.hidden.includes(key)}
                            onChange={() => setForm(toggleHidden(form, key))}
                          />
                        )}
                      </li>
                    );
                  })}
                {extras.map((point) => (
                  <li key={point.id} className={classes.point}>
                    <div className={classes.own}>
                      <div className={classes.lockedText}>
                        <Paragraph data-size="sm">{point.text}</Paragraph>
                        <Tag data-color="info" data-size="sm">
                          {t(`${K}.onlyThisArea`)}
                        </Tag>
                      </div>
                      <Button
                        data-size="sm"
                        variant="tertiary"
                        icon
                        aria-label={t(`${K}.removeOwn`, { text: point.text })}
                        onClick={() => setForm(removeExtra(form, point.id))}
                      >
                        <XMarkIcon aria-hidden="true" />
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
              <div className={classes.addRow}>
                <Field>
                  <Label htmlFor={inputId} data-size="sm">
                    {t(`${K}.ownPoint`)}
                  </Label>
                  <Input
                    id={inputId}
                    data-size="sm"
                    value={adding[category.key] ?? ''}
                    onChange={(event) => setAdding({ ...adding, [category.key]: event.target.value })}
                  />
                </Field>
                <Button
                  data-size="sm"
                  variant="secondary"
                  onClick={() => {
                    setForm(addExtra(form, category.key, adding[category.key] ?? ''));
                    setAdding({ ...adding, [category.key]: '' });
                  }}
                >
                  {t(`${K}.add`)}
                </Button>
              </div>
            </section>
          );
        })}
        {save.isError && (
          <Alert data-color="danger" data-size="sm">
            <Paragraph data-size="sm">{t('hms.errors.save')}</Paragraph>
          </Alert>
        )}
        <div className={layout.actions}>
          <Button
            data-size="sm"
            aria-busy={save.isPending || undefined}
            onClick={() => save.mutate(customizationInput(form), { onSuccess: () => navigate(planPath(plan.id)) })}
          >
            {t(`${K}.save`)}
          </Button>
          <Button asChild data-size="sm" variant="tertiary">
            <RouterLink to={planPath(plan.id)}>{t('safetyRounds.cancel')}</RouterLink>
          </Button>
        </div>
      </div>
      <aside className={layout.side}>
        <SuggestToHr plan={plan} />
      </aside>
    </div>
  );
};

/** A checkpoint that fits more areas goes to HR as a suggestion for the template */
const SuggestToHr = ({ plan }: { plan: PlanDetail }): ReactElement => {
  const { t } = useTranslation();
  const suggest = useSuggestCheckpointMutation(plan.templateId);
  const [text, setText] = useState('');
  const [categoryKey, setCategoryKey] = useState(plan.checklist[0]?.key ?? '');
  const [tried, setTried] = useState(false);
  const K = 'safetyRounds.customize.suggest';
  const empty = !text.trim();

  return (
    <section aria-labelledby="customize-suggest" className={layout.sideCard} data-gap="sm">
      <Heading level={2} data-size="2xs" id="customize-suggest">
        {t(`${K}.title`)}
      </Heading>
      <Paragraph data-size="sm">{t(`${K}.intro`)}</Paragraph>
      <Field>
        <Label htmlFor="customize-suggest-text">{t(`${K}.text`)}</Label>
        <Textarea
          id="customize-suggest-text"
          rows={2}
          value={text}
          aria-invalid={tried && empty ? true : undefined}
          onChange={(event) => setText(event.target.value)}
        />
        {tried && empty && <ValidationMessage>{t(`${K}.empty`)}</ValidationMessage>}
      </Field>
      <Field>
        <Label htmlFor="customize-suggest-category">{t(`${K}.category`)}</Label>
        <Select id="customize-suggest-category" value={categoryKey} onChange={(event) => setCategoryKey(event.target.value)}>
          {plan.checklist.map((category) => (
            <Select.Option key={category.key} value={category.key}>
              {category.number}. {category.name}
            </Select.Option>
          ))}
        </Select>
      </Field>
      {suggest.isSuccess && (
        <Paragraph data-size="sm" role="status">
          {t(`${K}.sent`)}
        </Paragraph>
      )}
      {suggest.isError && (
        <Alert data-color="danger" data-size="sm">
          <Paragraph data-size="sm">{t('hms.errors.save')}</Paragraph>
        </Alert>
      )}
      <Button
        data-size="sm"
        variant="secondary"
        aria-busy={suggest.isPending || undefined}
        onClick={() => {
          setTried(true);
          if (empty) return;
          suggest.mutate({ categoryKey, text: text.trim() }, { onSuccess: () => { setText(''); setTried(false); } });
        }}
      >
        {t(`${K}.send`)}
      </Button>
    </section>
  );
};
